import { z } from 'zod';
import { TemplateId } from './invitation';

const TEMPLATE_IDS: [TemplateId, ...TemplateId[]] = [
  'romantic', 'minimalist', 'rustic',
  'elegante', 'rustico', 'costero', 'urbano', 'romantico', 'epoca', 'moderno',
];

const optionalText = (max: number) =>
  z.string().max(max, `Máximo ${max} caracteres`);

const optionalUrl = z
  .string()
  .trim()
  .max(2048, 'URL demasiado larga')
  .refine(
    (v) => !v || /^https?:\/\/.+/i.test(v),
    'Debe ser una URL válida (http:// o https://)',
  );

const optionalTime = z
  .string()
  .refine(
    (v) => !v || /^\d{2}:\d{2}(:\d{2})?$/.test(v),
    'Formato de hora inválido (HH:MM)',
  );

const hexColor = z
  .string()
  .regex(/^#[0-9a-fA-F]{6}$/i, 'Color hex inválido (#RRGGBB)');

export const itineraryItemSchema = z.object({
  time: z
    .string()
    .regex(/^\d{2}:\d{2}(:\d{2})?$/, 'Formato de hora inválido (HH:MM)'),
  title: z.string().min(1, 'Título requerido').max(100, 'Máximo 100 caracteres'),
  description: z.string().max(300, 'Máximo 300 caracteres').optional(),
});

export const invitationSchema = z.object({
  bride_name: z
    .string()
    .min(1, 'El nombre de la novia es obligatorio')
    .max(80, 'Máximo 80 caracteres'),
  groom_name: z
    .string()
    .min(1, 'El nombre del novio es obligatorio')
    .max(80, 'Máximo 80 caracteres'),
  wedding_date: z
    .string()
    .min(1, 'La fecha de boda es obligatoria')
    .refine((v) => /^\d{4}-\d{2}-\d{2}/.test(v), 'Fecha inválida (YYYY-MM-DD)'),
  template_id: z.enum(TEMPLATE_IDS),
  primary_color: hexColor,
  secondary_color: hexColor,
  accent_color: hexColor,

  religious_ceremony_enabled: z.boolean(),
  religious_ceremony_name: optionalText(120),
  religious_ceremony_address: optionalText(300),
  religious_ceremony_time: optionalTime,
  religious_ceremony_map_url: optionalUrl,

  civil_ceremony_enabled: z.boolean(),
  civil_ceremony_name: optionalText(120),
  civil_ceremony_address: optionalText(300),
  civil_ceremony_time: optionalTime,
  civil_ceremony_map_url: optionalUrl,

  party_name: optionalText(120),
  party_address: optionalText(300),
  party_time: optionalTime,
  party_map_url: optionalUrl,

  dresscode: optionalText(120),
  itinerary: z.array(itineraryItemSchema).max(40, 'Máximo 40 entradas'),

  background_music_url: optionalUrl,
  background_music_enabled: z.boolean(),
  children_allowed: z.boolean(),

  gifts_message: optionalText(600),
  bank_transfer_enabled: z.boolean(),
  bank_account_holder: optionalText(120),
  bank_account_number: optionalText(60),
  bank_name: optionalText(80),
  cash_envelope_enabled: z.boolean(),
  gift_registry_url: optionalUrl,
  qr_transfer_enabled: z.boolean(),
  qr_image_url: optionalUrl,

  couple_gallery_enabled: z.boolean(),
  guest_photos_enabled: z.boolean(),
  guest_photos_auto_approve: z.boolean(), // 009: modo de aprobación del álbum

  // 004: campos de sección del Design System sin columna propia (JSONB `sections`).
  sections: z
    .object({
      fotos: z
        .object({
          portada: z.string().optional(),
          pareja: z.string().optional(),
          novia: z.string().optional(),
          novio: z.string().optional(),
          vestimenta: z.string().optional(),
          sedes: z
            .object({
              religiosa: z.string().optional(),
              civil: z.string().optional(),
              recepcion: z.string().optional(),
            })
            .partial()
            .optional(),
        })
        .partial()
        .optional(),
      fotosLugares: z.boolean().optional(),
      fotoVestimenta: z.boolean().optional(),
      historiaFrase: z.string().max(300).optional(),
      noviaNombreCompleto: z.string().max(120).optional(),
      novioNombreCompleto: z.string().max(120).optional(),
      retratos: z.enum(['pareja', 'individuales', 'sin']).optional(),
      vestimenta: z.object({ codigo: z.string().optional() }).partial().optional(),
      album: z.object({ hashtag: z.string().optional() }).partial().optional(),
      rsvpFechaLimite: z.string().max(80).optional(),
      itinerario: z
        .array(z.object({ momento: z.string(), momentoLibre: z.string().optional(), hora: z.string().optional(), detalle: z.string().optional() }))
        .max(40)
        .optional(),
    })
    .partial()
    .default({}),

  is_published: z.boolean(),
});

export type InvitationFormValues = z.infer<typeof invitationSchema>;

export const invitationDefaults: InvitationFormValues = {
  bride_name: '',
  groom_name: '',
  wedding_date: '',
  template_id: 'romantic',
  primary_color: '#6B7B3F',
  secondary_color: '#FFFFFF',
  accent_color: '#C9A962',
  religious_ceremony_enabled: false,
  religious_ceremony_name: '',
  religious_ceremony_address: '',
  religious_ceremony_time: '',
  religious_ceremony_map_url: '',
  civil_ceremony_enabled: false,
  civil_ceremony_name: '',
  civil_ceremony_address: '',
  civil_ceremony_time: '',
  civil_ceremony_map_url: '',
  party_name: '',
  party_address: '',
  party_time: '',
  party_map_url: '',
  dresscode: '',
  itinerary: [],
  background_music_url: '',
  background_music_enabled: true,
  children_allowed: true,
  gifts_message: '',
  bank_transfer_enabled: false,
  bank_account_holder: '',
  bank_account_number: '',
  bank_name: '',
  cash_envelope_enabled: false,
  gift_registry_url: '',
  qr_transfer_enabled: false,
  qr_image_url: '',
  couple_gallery_enabled: true,
  guest_photos_enabled: true,
  guest_photos_auto_approve: true,
  sections: {},
  is_published: false,
};
